/**
 * 1.7.0 stream C (docs/dev/formats-1.7.0-build.md): the Reframe lane under the ruler: the active format's framing keys
 * on the V1 clips and the selected layer clip's framing and motion keys, diamonds by ease (smooth, linear, hold).
 * Add a key at the playhead (the button or R), drag a key to retime it (moveKey, one undo step per drag: keyCoalesce,
 * sealed on pointer up), select one and press Delete to remove it, previous and next key (the buttons or [ and ]).
 * Keys outside a clip's visible part stay in the document but are not drawn.
 *
 * The lane is a row of the timeline's scrolling lanes; ReframeLaneLabel is its row in the track labels.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { ChevronLeft, ChevronRight, Diamond } from 'lucide-react';
import {
  KEY_EASE_LABELS, TICKS_PER_SECOND, framesToTimecode, formatTimecode, keyCoalesce, ops, sequenceFormatOf, ticksPerFrame, ticksToFrames,
  type FormatRef, type SequenceDocument,
} from '@prism/contracts';
import { isTyping } from '../../lib/keys';
import { toast } from '../../components/Toasts';
import type { EditorApply } from './formatContext';
import { addKeyAtPlayhead, adjacentKeyTime, findClip, laneKeys, retimedSource, spanOf, type LaneKey } from './edit-formats';

const S = TICKS_PER_SECOND;

export interface ReframeLaneProps {
  doc: SequenceDocument;
  /** The active format. */
  formatId: FormatRef;
  /** The selected clip (a layer clip shows its own keys too), or null. */
  selectedClipId: string | null;
  /** Pixels per second, as the timeline. */
  pps: number;
  /** Sequence ticks. */
  playhead: number;
  setPlayhead: (ticks: number) => void;
  apply: EditorApply;
  seal?: () => void;
  canEdit: boolean;
}

interface KeyDrag { key: LaneKey; startX: number; moved: boolean }

const keyId = (k: Pick<LaneKey, 'target' | 'index'>) => `${k.target.kind}:${k.target.clipId}:${k.target.formatId}:${k.index}`;

export function ReframeLane({ doc, formatId, selectedClipId, pps, playhead, setPlayhead, apply, seal, canEdit }: ReframeLaneProps) {
  const keys = useMemo(() => laneKeys(doc, formatId, selectedClipId), [doc, formatId, selectedClipId]);
  const [selected, setSelected] = useState<string | null>(null);
  const [drag, setDrag] = useState<KeyDrag | null>(null);
  const tpf = ticksPerFrame(doc.frameRate);
  const x = (ticks: number) => (ticks / S) * pps;
  const tc = (t: number) => formatTimecode(framesToTimecode(ticksToFrames(t, doc.frameRate), doc.frameRate));
  const selectedKey = keys.find((k) => keyId(k) === selected) ?? null;
  const format = sequenceFormatOf(doc, formatId);

  // A key that is gone (deleted, undone) is not selected.
  useEffect(() => { if (selected && !selectedKey && !drag) setSelected(null); }, [selected, selectedKey, drag]);

  const addKey = useCallback(() => {
    if (!canEdit) return;
    const a = addKeyAtPlayhead(doc, formatId, selectedClipId, playhead);
    if (!a) { toast('Move the playhead over a clip to add a key', 'error'); return; }
    apply(a.edit.fn, a.edit.coalesce);
    seal?.();
  }, [canEdit, doc, formatId, selectedClipId, playhead, apply, seal]);
  const remove = useCallback((k: LaneKey) => {
    if (!canEdit) return;
    apply((d) => ops.deleteKey(d, k.target, k.index));
    seal?.();
    setSelected(null);
  }, [canEdit, apply, seal]);
  const go = useCallback((dir: -1 | 1) => {
    const t = adjacentKeyTime(keys, playhead, dir, tpf);
    if (t !== null) setPlayhead(t);
  }, [keys, playhead, tpf, setPlayhead]);

  // R adds a key at the playhead, [ and ] go to the previous and next key, Delete removes the selected key. A capture
  // listener, so Delete with a key selected does not also remove the selected clip (Editor's shortcut).
  const latest = useRef({ addKey, remove, go, selectedKey });
  latest.current = { addKey, remove, go, selectedKey };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e, 'input, textarea, select, [contenteditable]') || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('.modal-backdrop')) return;
      const l = latest.current;
      if (e.key === 'r' || e.key === 'R') { if (e.shiftKey) return; e.preventDefault(); l.addKey(); }
      else if (e.key === '[') { e.preventDefault(); l.go(-1); }
      else if (e.key === ']') { e.preventDefault(); l.go(1); }
      else if ((e.key === 'Delete' || e.key === 'Backspace') && l.selectedKey) { e.preventDefault(); e.stopPropagation(); l.remove(l.selectedKey); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const down = (e: RPointerEvent, k: LaneKey) => {
    e.stopPropagation();
    setSelected(keyId(k));
    setPlayhead(k.recordTicks);
    if (!canEdit) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({ key: k, startX: e.clientX, moved: false });
  };
  const move = (e: RPointerEvent) => {
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    if (!drag.moved && Math.abs(dx) < 3) return;
    const span = spanOf(doc, drag.key.clipId);
    if (!span) return;
    const at = retimedSource(span, drag.key.sourceTicks, (dx / pps) * S, doc.frameRate);
    const { target, index } = drag.key;
    apply((d) => ops.moveKey(d, target, index, at), keyCoalesce(target, index));
    setPlayhead(span.recordIn + (at - span.sourceIn));
    if (!drag.moved) setDrag({ ...drag, moved: true });
  };
  const up = () => {
    if (!drag) return;
    setDrag(null);
    seal?.();
  };

  return (
    <div className="tl-lane reframe-lane" data-testid="reframe-lane" data-format={formatId} data-keys={keys.length}
      title={`Reframe keys of ${format?.label ?? 'the primary'}${canEdit ? ' · R adds a key at the playhead, drag to retime, Delete removes the selected key' : ''}`}
      onPointerDown={(e) => { e.stopPropagation(); setSelected(null); }}>
      {keys.map((k) => {
        const id = keyId(k);
        const clip = findClip(doc, k.clipId);
        return (
          <span key={id} className={`reframe-key ${k.ease} ${k.kind} ${selected === id ? 'selected' : ''} ${k.layer ? 'layer' : ''}`} style={{ left: x(k.recordTicks) }}
            data-testid="reframe-key" data-ease={k.ease} data-kind={k.kind} data-clip={k.clipId} data-index={k.index} data-at={k.sourceTicks}
            role="button" tabIndex={-1} aria-pressed={selected === id}
            aria-label={`${k.kind === 'framing' ? 'Framing' : k.kind === 'motion' ? 'Position' : 'Opacity'} key at ${tc(k.recordTicks)}`}
            title={`${clip?.clip.label ?? ''} · ${k.kind === 'framing' ? 'framing' : k.kind === 'motion' ? 'position and size' : 'opacity'} key · ${KEY_EASE_LABELS[k.ease]} · ${tc(k.recordTicks)}`}
            onPointerDown={(e) => down(e, k)} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
        );
      })}
    </div>
  );
}

/** The Reframe lane's row in the track labels: the format it shows, previous key, add a key, next key. */
export function ReframeLaneLabel({ doc, formatId, color, canEdit, onAdd, onPrev, onNext }: {
  doc: SequenceDocument; formatId: FormatRef; color: string; canEdit: boolean; onAdd: () => void; onPrev: () => void; onNext: () => void;
}) {
  const f = sequenceFormatOf(doc, formatId);
  return (
    <div className="tl-label reframe-label" data-testid="reframe-label">
      <span className="reframe-swatch" style={{ background: color }} aria-hidden />
      <span className="reframe-name" title={`Reframe keys · ${f?.label ?? ''}`}>{f?.label ?? 'Keys'}</span>
      <span style={{ flex: 1 }} />
      <button className="tl-mini" onClick={onPrev} aria-label="Previous key" title="Previous key ([)" data-testid="reframe-prev"><ChevronLeft size={11} /></button>
      {canEdit && <button className="tl-mini" onClick={onAdd} aria-label="Add a key at the playhead" title="Add a key at the playhead (R)" data-testid="reframe-add"><Diamond size={10} /></button>}
      <button className="tl-mini" onClick={onNext} aria-label="Next key" title="Next key (])" data-testid="reframe-next"><ChevronRight size={11} /></button>
    </div>
  );
}
